import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, BadgeCheck, Building2, Calendar, CreditCard, Landmark, Mail, MapPin, Phone, ShieldCheck, UserRound,
} from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/hooks/useData";
import { Avatar, Badge, Button, Card, InfoItem, Loader, SectionCard, StatusBadge } from "@/components/ui";
import { aed, dateLabel, daysUntil } from "@/lib/format";

const DOCS = [
  ["Emirates ID", "emirates_id", "emirates_id_expiry"],
  ["Passport", "passport", "passport_expiry"],
  ["UAE Visa", null, "visa_expiry"],
  ["Labour Card", "labour_card", "labour_card_expiry"],
  ["Health Insurance", null, "insurance_expiry"],
];

function expiryStatus(iso) {
  const n = daysUntil(iso);
  if (n < 0) return "Expired";
  if (n <= 30) return "Expiring Soon";
  return null;
}

export default function EmployeeProfile({ selfMode }) {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const empId = selfMode ? user.employee_id : params.id;
  const { data: emp } = useData(() => (empId ? api.employees.get(empId) : Promise.resolve(null)), [empId]);
  const { data: head } = useData(
    () => (emp?.reporting_head_id ? api.employees.get(emp.reporting_head_id) : Promise.resolve(null)),
    [emp?.reporting_head_id],
  );

  if (!empId) return <Card className="p-8 text-center text-sm text-slate-500">This account isn't linked to an employee record.</Card>;
  if (!emp) return <Loader />;

  return (
    <>
      {!selfMode && <Button variant="ghost" icon={<ArrowLeft size={14} />} onClick={() => navigate("/employees")}>All employees</Button>}

      <Card className="p-5 relative overflow-hidden">
        <div className="absolute -top-16 -right-10 h-40 w-40 rounded-full bg-brand-orange/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-4 flex-wrap">
          <Avatar name={emp.name} size="2xl" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{emp.id}</p>
            <h2 className="text-xl font-extrabold text-brand-blue tracking-tight">{emp.name}</h2>
            <p className="text-xs text-slate-500">{emp.designation} · {emp.department}</p>
            <div className="flex gap-2 mt-2">
              <Badge tone="green">{emp.employment_type}</Badge>
              {emp.is_manager && <Badge tone="brand">Manager</Badge>}
            </div>
          </div>
          <div className="neu-inset px-5 py-3 text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Monthly salary</p>
            <p className="text-xl font-extrabold text-brand-blue tabular-nums">AED {aed(emp.monthly_salary, 0)}</p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <SectionCard title="Personal & Employment" className="lg:col-span-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2">
            <InfoItem icon={Mail} label="Email" value={emp.email} />
            <InfoItem icon={Phone} label="Phone" value={emp.phone} />
            <InfoItem icon={Building2} label="Department" value={emp.department} />
            <InfoItem icon={BadgeCheck} label="Designation" value={emp.designation} />
            <InfoItem icon={Calendar} label="Date of joining" value={dateLabel(emp.date_of_joining)} />
            <InfoItem icon={MapPin} label="Work location" value={emp.work_location} />
            <InfoItem icon={ShieldCheck} label="Employment type" value={emp.employment_type} />
            <InfoItem icon={UserRound} label="Reporting head" value={head ? head.name : "—"} />
          </div>
        </SectionCard>

        <SectionCard title="UAE Documents" className="lg:col-span-2">
          <ul className="space-y-1">
            {DOCS.map(([label, numKey, expKey]) => {
              const status = expiryStatus(emp[expKey]);
              return (
                <li key={label} className="flex items-center gap-2.5 p-2 rounded-2xl hover:shadow-neu-xs transition-all">
                  <div className="h-9 w-9 shrink-0 rounded-xl shadow-neu-inset-xs flex items-center justify-center text-brand-blue"><CreditCard size={14} /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-brand-blue truncate">{label}</p>
                    <p className="text-[11px] text-slate-500 truncate">{numKey && <span className="font-mono">{emp[numKey]} · </span>}expires {dateLabel(emp[expKey])}</p>
                  </div>
                  {status && <StatusBadge status={status} />}
                </li>
              );
            })}
          </ul>
        </SectionCard>
      </div>

      <SectionCard title="Bank & Payroll" subtitle="WPS banking information">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
          <InfoItem icon={Landmark} label="Bank" value={emp.bank_name} />
          <InfoItem icon={CreditCard} label="IBAN" value={emp.iban} mono />
          <InfoItem icon={BadgeCheck} label="Monthly salary" value={`AED ${aed(emp.monthly_salary)}`} />
        </div>
        <div className="neu-divider my-5" />
        <p className="label">Salary breakdown (AED)</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.entries(emp.salary_components).map(([k, v]) => (
            <div key={k} className="neu-inset p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">{k}</p>
              <p className="text-sm font-extrabold text-brand-blue tabular-nums mt-1">{aed(v)}</p>
            </div>
          ))}
        </div>
      </SectionCard>
    </>
  );
}
